import type { Tutorial } from '../../shared/types.js';

export const blueprintsTutorial: Tutorial = {
  name: 'blueprints',
  title: 'Build a repo image agents launch from',
  description:
    'Create a blueprint straight from a GitHub repo: pick the repo, which names the image for you, build it on your base, set the install and startup commands, then launch agents into it.',
  category: 'blueprints',
  docPages: ['concepts/blueprints'],
  agenda: [
    'What a repo image is',
    'Connect a repository and name the image',
    'Add startup commands under Advanced',
    'Build it and launch agents into it',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'What a repo image is',
      body: "The quickest blueprint to build is a **repo image**: point it at a GitHub repo and Reflex builds it on your `base` with the repo cloned in, ready for agents to launch. Let's build one.",
      path: '/configure/build/environments',
    },
    {
      id: 'new',
      kind: 'action',
      trigger: 'click',
      title: 'Open the create form',
      body: 'Click the **New blueprint** button in the middle of the page. It opens on the **New Repo Image** flow: no Dockerfile to write, just a repo to point it at. Picking one names the image for you, and the name stays editable.',
      target: '[data-tutorial-id="blueprint-new"]',
      placement: 'left',
    },
    {
      id: 'repo',
      kind: 'info',
      title: 'Connect a repository',
      body: 'Pick a GitHub repo here. Reflex clones it into the image at build time and **auto-names** the blueprint after the repo. No GitHub connected? Connect it from this panel, or type a public repo as `owner/name`. To build an image with no repo at all, use **Create a custom blueprint** at the bottom of the form.',
      target: '[data-tutorial-id="blueprint-repo"]',
      placement: 'bottom',
      // First step on the dedicated create route. Declaring the path makes the
      // overlay navigate here even if the user did not arrive via "New blueprint".
      path: '/configure/build/environments/new',
    },
    {
      id: 'install',
      kind: 'info',
      title: 'The install command',
      body: 'Once a repo is connected, an **Install Command** field appears right here - `npm install`, `pip install -r requirements.txt`, whatever the project needs. It runs at **build time**, so dependencies are baked into the image and every agent boots with them ready.',
      target: '#blueprint-repository-section-content',
      placement: 'bottom',
    },
    {
      id: 'advanced-open',
      kind: 'action',
      trigger: 'click',
      title: 'Open Advanced for startup commands',
      body: 'Expand **Advanced** to reach the startup commands and the other optional settings.',
      target: '[data-testid="blueprint-advanced-section-toggle"]',
      placement: 'top',
      // Advances whether the user expands it now or it was already open, so the
      // step never strands or fights the section's state.
      advanceWhen: '[data-testid="blueprint-advanced-section-toggle"][aria-expanded="true"]',
    },
    {
      id: 'launch-commands',
      kind: 'info',
      title: 'The startup commands',
      body: 'These **Launch Commands** run on **every devbox boot**, just before the agent starts - not once at build like the install command. Use them to start a dev server, run migrations, or kick off `./start.sh`.',
      target: '[data-tutorial-id="blueprint-launch-commands"]',
      placement: 'top',
    },
    {
      id: 'create',
      kind: 'info',
      title: 'Build the blueprint',
      body: '**Create Blueprint** lights up once a repo is picked and the image has a name. Clicking it builds the image; when the build finishes, the blueprint is available to every agent in your org.',
      target: '[data-tutorial-id="blueprint-create-submit"]',
      placement: 'top',
    },
    {
      id: 'use-in-launch',
      kind: 'info',
      title: 'Launch agents into it',
      body: 'In the launch dialog, the **Blueprint** selector lists your blueprints. Pick this one and the agent boots straight into the image - repo cloned, dependencies installed, startup commands run - with no per-launch setup.',
    },
  ],
};
